import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Check, X, Pencil } from 'lucide-react';

import { getAvailableModules, assignModule, removeModule, updateModuleLicense } from '../../../services/tenantService';
import { formatDate } from '../../../utils/Utils';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import IconButton from '../../../components/ui/IconButton';
import Tooltip from '../../../components/ui/Tooltip';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

const ModuleCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #fff;
  border: 1.5px solid ${({ $assigned }) => ($assigned ? '#02D3BF' : '#E8ECF0')};
  border-radius: 6px;
  transition: border-color 0.15s;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ModuleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ModuleName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #004242;
`;

const ModuleCode = styled.span`
  font-size: 12px;
  color: #A0A8B4;
`;

const DefaultBadge = styled.span`
  font-size: 11px;
  color: #02D3BF;
  font-weight: 600;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  background: ${({ $assigned }) => ($assigned ? 'rgba(229, 57, 53, 0.08)' : 'rgba(2, 211, 191, 0.08)')};
  color: ${({ $assigned }) => ($assigned ? '#E53935' : '#02D3BF')};

  &:hover {
    background: ${({ $assigned }) => ($assigned ? 'rgba(229, 57, 53, 0.15)' : 'rgba(2, 211, 191, 0.15)')};
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 24px;
`;

const UsageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const UsageText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $tone }) => ($tone === 'error' ? '#E53935' : $tone === 'warning' ? '#F59E0B' : '#004242')};
`;

const UsageBarTrack = styled.div`
  height: 5px;
  border-radius: 3px;
  background: #E8ECF0;
  overflow: hidden;
`;

const UsageBarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $tone }) => ($tone === 'error' ? '#E53935' : $tone === 'warning' ? '#F59E0B' : '#02D3BF')};
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $tone }) => ($tone === 'danger' ? 'rgba(229, 57, 53, 0.1)' : 'rgba(245, 158, 11, 0.12)')};
  color: ${({ $tone }) => ($tone === 'danger' ? '#E53935' : '#F59E0B')};
`;

const ContractRow = styled.div`
  font-size: 11px;
  color: #A0A8B4;
`;

// ─────────────────────────────────────────────────────────────────────────────
// P4Ö-4 — BOŞ ALAN `""` DEĞİL `null` GİTMELİ.
//
// GenericFormDrawer sayı alanını `e.target.value` ile, tarih alanını `val || ''` ile yazar; alan
// boşaltılınca değer `""` olur. Backend DTO'su `int?` / `DateTime?` bekler ve System.Text.Json Web
// varsayılanları boş dizeyi OKUMAZ → `[ApiController]` otomatik 400 ProblemDetails döner (gövdede
// `message` yoktur, interceptor jenerik "Bir hata oluştu" toast'ı atar). Sonuç: "limiti kaldır"
// (SeatLimit → null, spec §5'in yaşam döngüsü) ve "sözleşme tarihini sil" akışları arayüzden HİÇ
// yapılamıyordu ve hata da anlamsızdı.
//
// `hardLimit` gövdeye HİÇ konmaz: hesaplanan değerdir (ceil(limit × (100+tolerans)/100)), API onu
// yok sayar ve formdan geri göndermek "düzenlenebilir alan" yanılgısı üretir.
//
// Tolerans boşaltılırsa 0 gider (alan `int`, null kabul etmez): "tolerans yok" okuması budur ve
// sert tavan limite eşitlenir.
export const buildLicensePayload = (formData = {}, confirmShift = false) => {
  const sayi = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const tarih = (v) => (v === '' || v === null || v === undefined ? null : v);
  const tolerans = sayi(formData.overageTolerancePercent);

  return {
    seatLimit: sayi(formData.seatLimit),
    overagePolicy: sayi(formData.overagePolicy) ?? 0,
    overageTolerancePercent: tolerans ?? 0,
    contractStartDate: tarih(formData.contractStartDate),
    contractEndDate: tarih(formData.contractEndDate),
    confirmContractYearShift: !!confirmShift,
  };
};

// P4Ö-3 — SÖZLEŞME YILI KAYMASI ONAY İSTER (API: 400 license.contractStartShiftRequiresConfirmation).
// Backend kuralının (TenantDataAccess.ContractYearShifts) arayüz aynası: kayıtlı tarih YOKSA onay
// istenmez (ilk giriş), yeni tarih YOKSA istenir (başlangıcı kaldırmak true-up penceresini kaldırır),
// varsa yalnız AY/GÜN değişimi ister (yıl ilerlemesi olağan yenilemedir).
// Ayna YANLIŞ karar verse bile kapı backend'dedir: 400 gelince onay alanı yine açılır.
export const contractYearShifts = (kayitli, yeni) => {
  const ayGun = (v) => {
    if (!v) return null;
    const parca = String(v).split('T')[0].split('-');
    if (parca.length < 3) return null;
    const [yil, ay, gun] = parca.map(Number);
    if (!yil || !ay || !gun) return null;
    return { ay, gun };
  };

  const eski = ayGun(kayitli);
  if (!eski) return false;

  const yeniDeger = ayGun(yeni);
  if (!yeniDeger) return true;

  return yeniDeger.ay !== eski.ay || yeniDeger.gun !== eski.gun;
};
// ─────────────────────────────────────────────────────────────────────────────

// Koltuk kullanımı (aktif + aşım) limite göre renklenir: %80 altı birincil,
// %80-100 arası turuncu uyarı, %100 üstü hata rengi (sert tavan aşılmış demektir).
const usageTone = (used, limit) => {
  if (!limit) return 'primary';
  const pct = (used / limit) * 100;
  if (pct > 100) return 'error';
  if (pct >= 80) return 'warning';
  return 'primary';
};

export default function TenantModulesTab({ tenantId }) {
  const { t, i18n } = useTranslation();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  // API onay istedi mi (400 license.contractStartShiftRequiresConfirmation). Aynamız onay
  // gerekmediğini düşünse bile alan açılır: karar backend'dedir.
  const [confirmRequired, setConfirmRequired] = useState(false);

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAvailableModules(tenantId);
      setModules(res.data?.data || []);
    } catch { /* interceptor */ }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const handleToggle = async (mod) => {
    setToggling(mod.moduleId);
    try {
      if (mod.isAssigned) {
        await removeModule(tenantId, mod.moduleId);
      } else {
        await assignModule(tenantId, mod.moduleId);
      }
      await fetchModules();
    } catch { /* interceptor */ }
    setToggling(null);
  };

  const handleEditLicense = useCallback((mod) => {
    setConfirmRequired(false);
    setEditingModule(mod);
  }, []);

  const handleSaveLicense = useCallback(async (formData) => {
    if (!editingModule) return false;
    try {
      await updateModuleLicense(
        tenantId,
        editingModule.moduleId,
        buildLicensePayload(formData, formData.confirmContractYearShift),
      );
      setConfirmRequired(false);
      await fetchModules();
      return true;
    } catch (error) {
      // Toast'ı interceptor attı. Onay isteniyorsa çekmece AÇIK kalır ve onay alanı görünür:
      // kullanıcı kutuyu işaretleyip aynı formu yeniden kaydeder.
      if (error?.response?.data?.message === 'license.contractStartShiftRequiresConfirmation') {
        setConfirmRequired(true);
      }
      return false;
    }
  }, [tenantId, editingModule, fetchModules]);

  // Onay alanı yalnız GEREKTİĞİNDE görünür (GenericFormDrawer `hidden(formData)` sözleşmesi):
  // ay/gün değişti ya da API onay istedi. MODAL YOK — aynı çekmecede bir uyarı bloğu + onay kutusu.
  const onayGerekli = useCallback(
    (formData) => confirmRequired
      || contractYearShifts(editingModule?.contractStartDate, formData?.contractStartDate),
    [confirmRequired, editingModule],
  );

  const licenseSchema = useMemo(() => ([
    {
      name: 'seatLimit', label: t('license.seatLimit'), type: 'number', grid: 6,
      // Kü-4 — `helperText` KALDIRILDI: GenericFormDrawer şema alanlarında yalnız
      // `field.description`'ı render ediyor (GenericFormDrawer.js:656), `helperText`
      // hiçbir yerde okunmuyordu (ölü alan, aynı metnin ikinci kopyası).
      description: t('license.seatLimitHelper'),
    },
    {
      name: 'overagePolicy', label: t('license.overagePolicy'), type: 'select', grid: 6,
      idKey: 'value', labelKey: 'label',
      options: [
        { value: 0, label: t('license.policyTolerate') },
        { value: 1, label: t('license.policyBlock') },
      ],
    },
    { name: 'overageTolerancePercent', label: t('license.tolerancePercent'), type: 'number', grid: 6 },
    { name: 'contractStartDate', label: t('license.contractStart'), type: 'date', grid: 6 },
    { name: 'contractEndDate', label: t('license.contractEnd'), type: 'date', grid: 6 },
    {
      name: 'contractShiftNotice', type: 'notice', tone: 'warning', grid: 12,
      hidden: (formData) => !onayGerekli(formData),
      content: t('license.contractShiftNotice'),
    },
    {
      name: 'confirmContractYearShift', type: 'checkbox', grid: 12,
      label: t('license.confirmContractShift'),
      hidden: (formData) => !onayGerekli(formData),
    },
  ]), [t, onayGerekli]);

  // useMemo ŞART: GenericFormDrawer initialValues DEĞİŞİRSE formData'yı sıfırlar
  // ([open, initialValues] etkisi). Her render'da yeni nesne üretilse, onay kutusu işaretlendikten
  // sonraki ilk üst-bileşen render'ı (setConfirmRequired) kullanıcının girdiğini SİLERDİ.
  const editInitialValues = useMemo(() => (editingModule ? {
    seatLimit: editingModule.seatLimit,
    overagePolicy: editingModule.overagePolicy,
    overageTolerancePercent: editingModule.overageTolerancePercent,
    contractStartDate: editingModule.contractStartDate,
    contractEndDate: editingModule.contractEndDate,
    confirmContractYearShift: false,
  } : null), [editingModule]);

  if (loading) return <EmptyText>{t('common.loading')}</EmptyText>;

  return (
    <Grid>
      {modules.map((mod) => {
        const used = (mod.activeSeats || 0) + (mod.overageSeats || 0);
        const hasLimit = mod.seatLimit !== null && mod.seatLimit !== undefined;
        const tone = hasLimit ? usageTone(used, mod.seatLimit) : 'primary';
        const remainingDays = mod.contractEndDate
          ? Math.ceil((new Date(mod.contractEndDate).getTime() - Date.now()) / 86400000)
          : null;

        return (
          <ModuleCard key={mod.moduleId} $assigned={mod.isAssigned || mod.isDefault}>
            <TopRow>
              <ModuleInfo>
                <ModuleName>{mod.name}</ModuleName>
                <ModuleCode>{mod.code}</ModuleCode>
                {mod.isDefault && <DefaultBadge>{t('tenant.defaultModule')}</DefaultBadge>}
              </ModuleInfo>
              <Actions>
                {/* İ-3 — etiket `license.rule` ("Kapsam Kuralı") DEĞİL: bu çekmece lisans
                    sayısını / aşım politikasını / tolerans yüzdesini / sözleşme tarihlerini
                    düzenliyor (licenseSchema). Kapsam kuralı bambaşka bir ekranda
                    (/licenses → LicenseRuleDrawer). */}
                {!mod.isDefault && mod.isAssigned && (
                  <Tooltip text={t('license.licenseSettings')}>
                    <IconButton
                      size="sm"
                      data-testid={`edit-license-${mod.moduleId}`}
                      onClick={() => handleEditLicense(mod)}
                    >
                      <Pencil size={14} />
                    </IconButton>
                  </Tooltip>
                )}
                {!mod.isDefault && (
                  <ToggleButton
                    $assigned={mod.isAssigned}
                    onClick={() => handleToggle(mod)}
                    disabled={toggling === mod.moduleId}
                  >
                    {mod.isAssigned ? <X size={16} /> : <Check size={16} />}
                  </ToggleButton>
                )}
              </Actions>
            </TopRow>

            {!mod.isDefault && (
              <UsageRow>
                {hasLimit ? (
                  <>
                    <UsageText $tone={tone}>{used} / {mod.seatLimit}</UsageText>
                    <UsageBarTrack style={{ flex: 1 }}>
                      <UsageBarFill $pct={(used / mod.seatLimit) * 100} $tone={tone} />
                    </UsageBarTrack>
                  </>
                ) : (
                  <UsageText $tone="primary">{t('license.unlimited')}</UsageText>
                )}
              </UsageRow>
            )}

            {!mod.isDefault && (mod.overageSeats > 0 || mod.blockedSeats > 0) && (
              <BadgeRow>
                {mod.overageSeats > 0 && (
                  <Badge data-testid="overage-badge" $tone="warning">
                    {t('license.overageSeats')}: {mod.overageSeats}
                  </Badge>
                )}
                {mod.blockedSeats > 0 && (
                  <Badge data-testid="blocked-badge" $tone="danger">
                    {mod.blockedSeats} {t('license.blockedSeats')}
                  </Badge>
                )}
              </BadgeRow>
            )}

            {!mod.isDefault && mod.contractEndDate && (
              <ContractRow>
                {/* Kü-6 — formatDate(value, locale = 'tr'): dil geçilmezse sözleşme bitişi
                    İngilizce/Rusça arayüzde de tr biçiminde çıkıyordu (config/index.js
                    dateFilterColumn'a `lang` geçerek doğrusunu yapıyor). */}
                {t('license.contractEnd')}: {formatDate(mod.contractEndDate, i18n.language)}
                {remainingDays !== null && remainingDays >= 0 && (
                  <> · {t('license.expiresInDays', { days: remainingDays })}</>
                )}
              </ContractRow>
            )}
          </ModuleCard>
        );
      })}

      <div data-testid={editingModule ? 'license-drawer' : undefined}>
        <GenericFormDrawer
          open={!!editingModule}
          onClose={() => { setEditingModule(null); setConfirmRequired(false); }}
          title={editingModule ? `${editingModule.name} — ${t('license.licenseSettings')}` : ''}
          schema={licenseSchema}
          initialValues={editInitialValues}
          onSave={handleSaveLicense}
        />
      </div>
    </Grid>
  );
}
